import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Formik, Form } from 'formik';

import {
  FILE_PARSER_TYPE_INTERNET_DISCOUNT_ELIGIBILITIES,
  FILE_PARSER_TYPE_OPTIONS,
  FILE_PARSER_TYPE_ADDRESS_IMPORT,
  FILE_PARSER_TYPE_RENT_ROLL,
  UTILITY_BULK_IMPORT_SOURCE_ADMIN_UPLOAD,
} from 'common/utility/constants';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { MEDIA_QUERY_DESKTOP_SCREEN } from 'client/constants/mediaQueries';
import {
  SR_API_ACTION_UTILITY_BULK_FILE_IMPORT_PROCESS,
  SR_API_ACTION_UTILITY_BULK_FILE_IMPORT_TEST,
  SR_API_ACTION_UTILITY_PARTNER_GET_BY_AUTOCOMPLETE,
  SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE,
} from 'api/utilityActionTypes';

import {
  hideFieldIf,
  createFormValidationFn,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrUtilityProfitOptionCard from 'client/components/SrUtilityProfitOptionCard/SrUtilityProfitOptionCard';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY,
} from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';

import './UPFileImportAdminModal.less';

const getFormFields = ({
  getUtilityPartners,
  selectedUtilityPartner,
  values,
  getUtilityProviders,
  selectedUtilityProvider,
}) => {
  return [
    {
      id: 'utilityPartnerId',
      type: 'hidden',
    },
    {
      id: 'utilityProviderId',
      type: 'hidden',
    },
    {
      id: 'fileType',
      type: 'select',
      title: 'File Type',
      properties: {
        placeholder: 'Select file type',
        enableResetOption: true,
        choices: FILE_PARSER_TYPE_OPTIONS,
      },
      validations: ['required'],
    },
    hideFieldIf(values.fileType === FILE_PARSER_TYPE_INTERNET_DISCOUNT_ELIGIBILITIES, {
      id: 'searchPartnerInput',
      title: selectedUtilityPartner?.name
        ? `Partner: ${selectedUtilityPartner.name}`
        : 'Select a partner',
      type: 'autocomplete',
      properties: {
        placeholder: selectedUtilityPartner?.name
          ? 'Change selected partner...'
          : 'Select a partner...',
        choices: [],
        onSuggestionsFetchRequested: ({ value }) => {
          return getUtilityPartners({ searchPartnerInput: value });
        },
        filterFunc: () => {
          return true;
        },
      },
    }),
    hideFieldIf(values.fileType !== FILE_PARSER_TYPE_INTERNET_DISCOUNT_ELIGIBILITIES, {
      id: 'searchProviderInput',
      title: selectedUtilityProvider?.title
        ? `Provider: ${selectedUtilityProvider.title}`
        : 'Select a provider',
      type: 'autocomplete',
      properties: {
        placeholder: selectedUtilityProvider?.title
          ? 'Change selected provider...'
          : 'Select a provider...',
        choices: [],
        onSuggestionsFetchRequested: ({ value }) => {
          return getUtilityProviders({ searchProviderInput: value });
        },
        filterFunc: () => {
          return true;
        },
      },
    }),
    {
      id: 'fileUrl',
      type: 'file_single',
      title: 'File to import',
      properties: {
        fileAccept: '*',
        filePath: '/up-file-imports/{uuid}',
      },
      validations: ['required'],
    },
  ];
};

const UPFileImportAdminModal = ({ onClose }) => {
  const [selectedUtilityPartner, setSelectedUtilityPartner] = useState(null);
  const [selectedUtilityProvider, setSelectedUtilityProvider] = useState(null);
  const [utilityPartners, setUtilityPartners] = useState([]);
  const [utilityProviders, setUtilityProviders] = useState([]);
  const [testResults, setTestResults] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDesktopScreen = useMediaQuery({
    query: MEDIA_QUERY_DESKTOP_SCREEN,
  });

  const dispatch = useDispatch();

  const getUtilityPartners = ({ searchPartnerInput }) => {
    if (!searchPartnerInput) {
      setUtilityPartners([]);
      return;
    }

    return dispatch(requestUtilityApi, {
      queryId: searchPartnerInput,
      action: SR_API_ACTION_UTILITY_PARTNER_GET_BY_AUTOCOMPLETE,
      payload: {
        searchInput: searchPartnerInput,
        limit: 4,
      },
    }).then((autocompletePredictions) => {
      setUtilityPartners(autocompletePredictions || []);
    });
  };

  const getUtilityProviders = ({ searchProviderInput }) => {
    if (!searchProviderInput) {
      setUtilityProviders([]);
      return;
    }

    return dispatch(requestUtilityApi, {
      queryId: searchProviderInput,
      action: SR_API_ACTION_UTILITY_PROVIDER_GET_BY_AUTOCOMPLETE,
      payload: {
        searchInput: searchProviderInput,
        utilityTypeIds: [1],
        limit: 4,
      },
    }).then((autocompletePredictions) => {
      setUtilityProviders(autocompletePredictions || []);
    });
  };

  const handleProcessFile = ({ formValues }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_BULK_FILE_IMPORT_PROCESS,
      payload: {
        utilityPartnerId: formValues.utilityPartnerId,
        fileType: formValues.fileType,
        fileUrl: formValues.fileUrl,
        utilityProviderId: formValues.utilityProviderId,
        source: UTILITY_BULK_IMPORT_SOURCE_ADMIN_UPLOAD,
      },
    }).then(() => {
      setIsSubmitting(false);
      return onClose();
    });
  };

  const handleTestFile = ({ formValues }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_BULK_FILE_IMPORT_TEST,
      payload: {
        utilityPartnerId: formValues.utilityPartnerId,
        fileType: formValues.fileType,
        utilityProviderId: formValues.utilityProviderId,
        fileUrl: formValues.fileUrl,
        source: UTILITY_BULK_IMPORT_SOURCE_ADMIN_UPLOAD,
      },
    }).then((response) => {
      setIsSubmitting(false);
      return setTestResults(response);
    });
  };

  const validateSubmit = ({ formikBag, formFields }) => {
    const formValues = formikBag.values;
    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFieldIdsWithErrors = Object.keys(formErrors);
    // TODO @Gustavo: This is a temporary solution to check if the partner is required.
    // This should be refactored so each parser type has its own validation.
    const isPartnerRequired = [
      FILE_PARSER_TYPE_ADDRESS_IMPORT,
      FILE_PARSER_TYPE_RENT_ROLL,
    ].includes(formValues.fileType);

    const isProviderRequired = [FILE_PARSER_TYPE_INTERNET_DISCOUNT_ELIGIBILITIES].includes(
      formValues.fileType,
    );

    if (!formValues.utilityPartnerId && isPartnerRequired) {
      formikBag.setFieldError('searchPartnerInput', 'Please select a partner.');
      formikBag.setFieldTouched('searchPartnerInput', true, false);
      scrollToFormField({
        formFieldId: 'searchPartnerInput',
      });
      return false;
    }

    if (!formValues.utilityProviderId && isProviderRequired) {
      formikBag.setFieldError('searchProviderInput', 'Please select a provider.');
      formikBag.setFieldTouched('searchProviderInput', true, false);
      scrollToFormField({
        formFieldId: 'searchProviderInput',
      });
      return false;
    }

    if (formFieldIdsWithErrors.length) {
      formFieldIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFieldIdsWithErrors[0],
      });
      return false;
    }

    return true;
  };

  const onProcessFile = ({ formikBag, formFields }) => {
    setIsSubmitting(true);
    setTestResults(null);
    const isValid = validateSubmit({ formikBag, formFields });
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }
    return handleProcessFile({ formValues: formikBag.values });
  };

  const onTestFile = ({ formikBag, formFields }) => {
    setIsSubmitting(true);
    setTestResults(null);
    const isValid = validateSubmit({ formikBag, formFields });
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }
    return handleTestFile({ formValues: formikBag.values });
  };

  return (
    <Formik
      initialValues={{}}
      validate={(formValues) => {
        const formFields = getFormFields({
          getUtilityPartners,
          selectedUtilityPartner,
          getUtilityProviders,
          selectedUtilityProvider,
        });
        return createFormValidationFn(formFields)(formValues);
      }}
    >
      {(formikBag) => {
        const formFields = getFormFields({
          getUtilityPartners,
          selectedUtilityPartner,
          values: formikBag.values,
          getUtilityProviders,
          selectedUtilityProvider,
        });
        const searchPartnerInput = formikBag.values?.searchPartnerInput;
        const searchProviderInput = formikBag.values?.searchProviderInput;
        return (
          <SrModalV2
            theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            contentMaxHeight="700px"
            contentPadding="28px 20px 100px 20px"
            buttonContainerProps={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
            buttonProps={[
              {
                text: isSubmitting ? 'Processing...' : 'Submit',
                disabled: isSubmitting,
                theme: SR_BUTTON_THEME_ADOBE_GREEN,
                onClick: () => onProcessFile({ formikBag, formFields }),
              },
              {
                text: 'Test',
                theme: SR_BUTTON_THEME_ADOBE_BLUE,
                onClick: () => onTestFile({ formikBag, formFields }),
              },
              {
                text: 'Close',
                theme: SR_BUTTON_THEME_ADOBE_GREY,
                onClick: onClose,
              },
            ]}
          >
            <SrBox
              styleName="UPFileImportAdminModal"
              maxHeight={isDesktopScreen ? 'calc(80vh - 210px)' : 'calc(100vh - 300px)'}
              padding={!isDesktopScreen ? '0 15px 100px 20px' : '0 15px 80px 20px'}
              boxSizing="border-box"
              overflow="auto"
            >
              <div styleName="header">
                <span styleName="title">Import File</span>
              </div>
              <Form className="form">
                {formFields.map((formField) => (
                  <div key={formField.id} styleName="partnerOption">
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY}
                      margin={formField.margin}
                    />
                    {searchPartnerInput &&
                      utilityPartners.length === 0 &&
                      formField.id === 'searchPartnerInput' && (
                        <div styleName="non-existent">
                          <span styleName="text">
                            That partner does not currently exist in our system.
                          </span>
                        </div>
                      )}
                    {searchPartnerInput &&
                      formField.id === 'searchPartnerInput' &&
                      utilityPartners.map((utilityPartner) => {
                        return (
                          <SrBox key={utilityPartner.id}>
                            <SrUtilityProfitOptionCard
                              title={utilityPartner.name}
                              subtitle={<>{utilityPartner.slug}</>}
                              detailColor="white"
                              imageUrl={utilityPartner.logoUrl}
                              onAddClick={() => {
                                setSelectedUtilityPartner(utilityPartner);
                                setSelectedUtilityProvider(null);
                                formikBag.setFieldValue('searchPartnerInput', '');
                                formikBag.setFieldValue('utilityPartnerId', utilityPartner.id);
                              }}
                            />
                          </SrBox>
                        );
                      })}
                    {searchProviderInput &&
                      utilityProviders.length === 0 &&
                      formField.id === 'searchProviderInput' && (
                        <div styleName="non-existent">
                          <span styleName="text">
                            That provider does not currently exist in our system.
                          </span>
                        </div>
                      )}
                    {searchProviderInput &&
                      formField.id === 'searchProviderInput' &&
                      utilityProviders.map((utilityProvider) => {
                        return (
                          <SrBox key={utilityProvider.id}>
                            <SrUtilityProfitOptionCard
                              title={utilityProvider.title}
                              detailColor="white"
                              imageUrl={utilityProvider.logoUrl}
                              onAddClick={() => {
                                setSelectedUtilityProvider(utilityProvider);
                                setUtilityPartners([]);
                                formikBag.setFieldValue('searchProviderInput', '');
                                formikBag.setFieldValue('utilityProviderId', utilityProvider.id);
                              }}
                            />
                          </SrBox>
                        );
                      })}
                  </div>
                ))}
              </Form>
              {testResults && (
                <div styleName="testResults">
                  <div styleName="title">Test Results</div>
                  <pre styleName="output">{JSON.stringify(testResults, null, 2)}</pre>
                </div>
              )}
            </SrBox>
          </SrModalV2>
        );
      }}
    </Formik>
  );
};

UPFileImportAdminModal.propTypes = {};

UPFileImportAdminModal.displayName = 'UPFileImportAdminModal';

export default UPFileImportAdminModal;
